import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { SlideMenuService } from '../../services/slide-menu.service';
import { Menu } from '../../models/menu.model';

@Component({
  selector: 'app-menu-item',
  templateUrl: './menu-item.component.html',
  styleUrl: './menu-item.component.scss'
})
export class MenuItemComponent implements OnInit, OnChanges, OnDestroy {

  @Input() menuItem: Menu | undefined;

  // Indica si el grupo de hijos esta desplegado
  public isOpen = false;

  // Indica si la ruta de este item es la pagina actual
  public isActive = false;

  // Indica si alguno de los hijos de este item es la pagina actual
  public hasActiveChild = false;

  private routerSubscription: Subscription | undefined;

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    public slideMenuService: SlideMenuService
  ) {}

  ngOnInit() {

    // Cada navegacion puede cambiar el item seleccionado
    this.routerSubscription = this.router.events
      .pipe(filter((routerEvent) => routerEvent instanceof NavigationEnd))
      .subscribe(() => this.updateActiveState());

    this.updateActiveState();

  }

  ngOnChanges() {

    // El layout de colonia reasigna el menu al resolver el neighborhoodId
    this.updateActiveState();

  }

  ngOnDestroy() {

    // side-menu se destruye al cerrar el cajon movil, hay que dar de baja la suscripcion
    this.routerSubscription?.unsubscribe();

  }

  /*
  ██╗   ██╗██████╗ ██████╗  █████╗ ████████╗███████╗     █████╗  ██████╗████████╗██╗██╗   ██╗███████╗    ███████╗████████╗ █████╗ ████████╗███████╗
  ██║   ██║██╔══██╗██╔══██╗██╔══██╗╚══██╔══╝██╔════╝    ██╔══██╗██╔════╝╚══██╔══╝██║██║   ██║██╔════╝    ██╔════╝╚══██╔══╝██╔══██╗╚══██╔══╝██╔════╝
  ██║   ██║██████╔╝██║  ██║███████║   ██║   █████╗      ███████║██║        ██║   ██║██║   ██║█████╗      ███████╗   ██║   ███████║   ██║   █████╗  
  ██║   ██║██╔═══╝ ██║  ██║██╔══██║   ██║   ██╔══╝      ██╔══██║██║        ██║   ██║╚██╗ ██╔╝██╔══╝      ╚════██║   ██║   ██╔══██║   ██║   ██╔══╝  
  ╚██████╔╝██║     ██████╔╝██║  ██║   ██║   ███████╗    ██║  ██║╚██████╗   ██║   ██║ ╚████╔╝ ███████╗    ███████║   ██║   ██║  ██║   ██║   ███████╗
   ╚═════╝ ╚═╝     ╚═════╝ ╚═╝  ╚═╝   ╚═╝   ╚══════╝    ╚═╝  ╚═╝ ╚═════╝   ╚═╝   ╚═╝  ╚═══╝  ╚══════╝    ╚══════╝   ╚═╝   ╚═╝  ╚═╝   ╚═╝   ╚══════╝

  */
  updateActiveState() {

    this.isActive = this.isRouteActive(this.menuItem?.route, this.menuItem?.exact === true);

    this.hasActiveChild = (this.menuItem?.children ?? []).some(
      (child) => this.isRouteActive(child.route, child.exact === true)
    );

    // Solo se abre de forma automatica, nunca se cierra para no quitarle el control al usuario
    if(this.hasActiveChild) {
      this.isOpen = true;
    }

  }

  /*
  ██╗███████╗    ██████╗  ██████╗ ██╗   ██╗████████╗███████╗     █████╗  ██████╗████████╗██╗██╗   ██╗███████╗
  ██║██╔════╝    ██╔══██╗██╔═══██╗██║   ██║╚══██╔══╝██╔════╝    ██╔══██╗██╔════╝╚══██╔══╝██║██║   ██║██╔════╝
  ██║███████╗    ██████╔╝██║   ██║██║   ██║   ██║   █████╗      ███████║██║        ██║   ██║██║   ██║█████╗  
  ██║╚════██║    ██╔══██╗██║   ██║██║   ██║   ██║   ██╔══╝      ██╔══██║██║        ██║   ██║╚██╗ ██╔╝██╔══╝  
  ██║███████║    ██║  ██║╚██████╔╝╚██████╔╝   ██║   ███████╗    ██║  ██║╚██████╗   ██║   ██║ ╚████╔╝ ███████╗
  ╚═╝╚══════╝    ╚═╝  ╚═╝ ╚═════╝  ╚═════╝    ╚═╝   ╚══════╝    ╚═╝  ╚═╝ ╚═════╝   ╚═╝   ╚═╝  ╚═══╝  ╚══════╝

  */
  isRouteActive(route: string | undefined, exact: boolean): boolean {

    if(!route) {
      return false;
    }

    // Se reproduce la misma forma que usa routerLink con un string: commands = [route]
    const urlTree = this.router.createUrlTree([route], { relativeTo: this.activatedRoute });

    return this.router.isActive(
      urlTree,
      {
        paths: exact ? 'exact' : 'subset',
        queryParams: 'ignored',
        fragment: 'ignored',
        matrixParams: 'ignored'
      }
    );

  }

}
